import Swiper from 'swiper';
import {EffectFade, EffectCreative, Navigation, Pagination, Thumbs, Scrollbar, Autoplay, FreeMode, Parallax} from 'swiper/modules';
import EffectPanorama from './effect-panorama.esm.js';
// import EffectExpo from './effect-expo.esm.js';

class SwiperCarousel {
	constructor(config) {
		this.config = Object.assign({
			scope: '.swiper',
		}, config);

		this.elements = document.querySelectorAll(this.config.scope);

		if (this.elements.length > 0) {
			this.init();
		}
	}

	init() {
		// Initialiseer eerst normale swipers, dan delayed
		this.#initializeBySelector(`${this.config.scope}:not(.delay-init)`);
		this.#initializeBySelector(`${this.config.scope}.delay-init`);
		this.#initSliderControls();
	}

	#initializeBySelector(selector) {
		const elements = document.querySelectorAll(selector);
		elements.forEach(el => {
			this.#initialiseSingleObject(el);
		});
	}

	async #initialiseSingleObject(element) {
		const settings = this.#buildSettings(element);
		const container = element.closest(settings.wrapperEl);

		if (settings.minSlides > 0) {
			await this.#cloneSlides(element, settings.minSlides);
		}

		const swiper = this.#createSwiper(element, container, settings);
		this.#hideNavigationIfNeeded(element, swiper, settings);

		if (settings?.scrollMode === true) {
			this.#initScrollMode(swiper);
		}

		if (settings?._postersEffect === true) {
			this.#initPostersResize(element, swiper);
		}

		if (settings?.smooth === true) {
			this.#initSmoothSwipe(swiper, settings?.speed ?? 5000);
		}

		if (settings.autoplay && settings.autoplayInView !== false) {
			this.#observeAutoplay(element, swiper);
		}

		if (settings?.direction === 'vertical') {
			this.#observeVerticalSwipe(element, swiper);
		}
	}

	#initSmoothSwipe(swiper, speed) {
		console.log(swiper.params.autoplay);
		const reverse = swiper.el.dataset.reverse === 'true';
		const dir = reverse ? -1 : 1;
		const pxPerMs = (speed / 1000) * dir;
		let last = null;
		let paused = false;

		if (reverse) {
			swiper.setTranslate(swiper.translate - swiper.virtualSize / 2);
			swiper.updateActiveIndex();
			swiper.updateSlidesClasses?.();
		}

		const initialTranslate = swiper.translate;

		// Pauzeer bij hover als pauseOnMouseEnter aan staat
		if (swiper.el.dataset.pauseOnHover === 'true') {
			swiper.el.addEventListener('mouseenter', () => paused = true);
			swiper.el.addEventListener('mouseleave', () => {
				paused = false;
				last = null;
			});
		}

		const tick = (ts) => {
			if (swiper.destroyed) return;

			if (!paused && last !== null) {
				let t = swiper.translate - pxPerMs * (ts - last);

				const half = swiper.virtualSize / 2;
				if (!reverse && half > 0 && t < initialTranslate - half) t += half;
				if (reverse  && half > 0 && t > initialTranslate + half) t -= half;

				swiper.setTranslate(t);
				swiper.updateActiveIndex();
				swiper.updateSlidesClasses?.();
			}

			last = paused ? null : ts;
			requestAnimationFrame(tick);
		};

		swiper.wrapperEl.style.transitionDuration = '0ms';
		swiper.wrapperEl.style.transitionTimingFunction = 'linear';
		swiper.wrapperEl.style.willChange = 'transform';
		requestAnimationFrame(tick);
	}

	#initScrollMode(swiper) {
		const el = swiper.el;
		const reverse = el.dataset.reverse === 'true';

		const update = () => {
			const { top, bottom } = el.getBoundingClientRect();
			const viewH = window.innerHeight;
			const elH = el.offsetHeight;

			// Start when fully inside viewport + 30px, end 100px before top
			const startTop = viewH - elH - 50; // top position when element just fully entered + 30px
			const endTop = 140;                // top position when animation completes

			// Outside range — clamp
			if (top >= startTop) {
				swiper.setTranslate(reverse ? swiper.maxTranslate() : swiper.minTranslate());
				return;
			}
			if (top <= endTop) {
				swiper.setTranslate(reverse ? swiper.minTranslate() : swiper.maxTranslate());
				return;
			}

			const progress = Math.min(1, Math.max(0, (startTop - top) / (startTop - endTop)));
			const effectiveProgress = reverse ? 1 - progress : progress;
			const t = swiper.minTranslate() + (swiper.maxTranslate() - swiper.minTranslate()) * effectiveProgress;

			swiper.setTranslate(t);
			swiper.updateActiveIndex();
			swiper.updateSlidesClasses?.();
		};

		window.addEventListener('scroll', update, { passive: true });
		update();
	}

	#initPostersResize(element, swiper) {
		const swiperEl = element;
		const calcNextOffset = () => {
			const parentWidth = swiperEl.parentElement.offsetWidth;
			const swiperWidth = swiperEl.offsetWidth;
			let nextOffset = (parentWidth - (parentWidth - swiperWidth) / 2) / swiperWidth;
			nextOffset = Math.max(nextOffset, 1);
			return `${nextOffset * 100}%`;
		};

		swiper.params.creativeEffect.next.translate = [calcNextOffset(), 0, 0];
		swiper.update();

		window.addEventListener('resize', () => {
			if (!swiper || swiper.destroyed) return;
			swiper.params.creativeEffect.next.translate = [calcNextOffset(), 0, 0];
			swiper.update();
		});
	}

	#observeAutoplay(element, swiper) {
		swiper.autoplay.stop();

		const observer = new IntersectionObserver((entries) => {
			entries.forEach(entry => {
				if (entry.isIntersecting) {
					swiper.autoplay.start();
				} else {
					swiper.autoplay.stop();
				}
			});
		}, { threshold: 0 });

		observer.observe(element);
	}

	#observeVerticalSwipe(element, swiper) {
		if (window.innerWidth >= 992) return;

		const disable = () => {
			swiper.allowTouchMove = false;
			swiper.el.style.touchAction = 'pan-y';
			swiper.el.style.cursor = 'default';
		};

		const enable = () => {
			swiper.allowTouchMove = true;
			swiper.el.style.touchAction = '';
			swiper.el.style.cursor = '';
		};

		swiper.on('reachEnd', disable);
		swiper.on('reachBeginning', disable);
		swiper.on('fromEdge', enable);

		swiper.on('slideChange', () => {
			if (!swiper.isEnd && !swiper.isBeginning) {
				enable();
			}
		});

		// Re-enable bij scroll richting verandering
		let lastScrollY = window.scrollY;

		window.addEventListener('scroll', () => {
			const currentScrollY = window.scrollY;
			const isScrollingUp = currentScrollY < lastScrollY;
			const isScrollingDown = currentScrollY > lastScrollY;

			if (!swiper.allowTouchMove) {
				if (isScrollingUp && swiper.isEnd) {
					enable();
				} else if (isScrollingDown && swiper.isBeginning) {
					enable();
				}
			}

			lastScrollY = currentScrollY;
		}, { passive: true });
	}

	#initSliderControls() {
		const controls = document.querySelectorAll('[data-slider-control]');

		controls.forEach(control => {
			const targetSelector = control.getAttribute('data-slider-control');
			const wrapper = document.getElementById(targetSelector);
			const swiperElement = wrapper?.querySelector('.swiper');

			if (swiperElement?.swiper) {
				swiperElement.swiper.once('slideChange', () => {
					control.style.opacity = '1';
				});
				control.addEventListener('click', (e) => {
					e.preventDefault();
					this.#toggleSlider(control, swiperElement);
				});
			}
		});
	}

	#toggleSlider(control, swiperElement) {
		const swiper = swiperElement.swiper;
		if (swiper.autoplay.running) {
			swiper.autoplay.stop();
			control.classList.add('swiper-is-paused');
			control.classList.remove('swiper-is-playing');
		} else {
			swiper.autoplay.start();
			control.classList.add('swiper-is-playing');
			control.classList.remove('swiper-is-paused');
		}
	}

	#buildSettings(element) {
		const data = {...this.#getDataAttributes(element)};
		delete data.config;

		let settings = Object.assign(this.#defaultSettings(), data);

		// Merge config object indien aanwezig
		const configAttr = element.getAttribute('data-config');
		if (configAttr) {
			try {
				const configData = JSON.parse(configAttr);
				settings = {...settings, ...configData};
			} catch (e) {
				console.error('Invalid data-config JSON:', e);
			}
		}

		// Autoplay configuratie
		if (settings.delay) {
			settings.autoplay = {
				delay: settings.delay,
				pauseOnMouseEnter: settings.pauseOnMouseEnter !== false,
				disableOnInteraction: false,
				reverseDirection: settings.reverse === true,
			};
		}

		// Link thumb swiper indien aanwezig
		if (settings.thumbs?.swiper) {
			const thumbElement = document.querySelector(settings.thumbs.swiper);
			settings.thumbs.swiper = thumbElement?.swiper;
		}

		if (settings?.effect === 'fade') {
			settings.modules.push(EffectFade);
			settings.fadeEffect = {crossFade: true};
		}

		if (settings?.effect === 'panorama') {
			settings.modules.push(EffectPanorama);

			settings.smooth = true;
			settings.slidesPerView = 7;
			settings.centeredSlides = true;
			settings.initialSlide = 3;
			settings.loopAdditionalSlides = 4; // Buffer to prevent loop-end stutter
			settings.minSlides = 16;
			settings.breakpoints = {
				0:    { slidesPerView: 2.1 },
				768:  { slidesPerView: 3.4 },
				992:  { slidesPerView: 4.2 },
				1440: { slidesPerView: 4.55 },
				1600: { slidesPerView: 6 },
				1800: { slidesPerView: 6.8 },
			};
		}

		if (settings?.smooth === true) {
			delete settings.autoplay;
			settings.autoplayInView = false;
			settings.allowTouchMove = false;
			settings.grabCursor = false;
			settings.speed = 80;
		}

		if (settings?.scrollMode === true) {
			delete settings.autoplay;
			settings.autoplayInView = false;
			settings.allowTouchMove = false;
			settings.grabCursor = false;
		}

		if (settings?.effect === 'posters') {
			settings.modules.push(EffectCreative, Parallax);
			settings.effect = 'creative';
			settings.parallax = true;
			settings.speed = 400;
			settings.resistanceRatio = 0;
			settings.loop = true;
			settings._postersEffect = true;
			settings.creativeEffect = {
				limitProgress: 3,
				perspective: true,
				shadowPerProgress: true,
				prev: {
					scale: 0.9,
					origin: 'center top',
				},
				next: {
					scale: 0.75,
					opacity: 0.26,
					origin: 'left top',
					translate: ['105%', 0, 0],
				},
			};
		}

		// if (settings?.effect === 'expo') {
		// 	settings.modules.push(EffectExpo);
		// 	settings.slidesPerView = 1.5;
		// 	settings.loop = true;
		// 	settings.minSlides = 6;
		// 	settings.expoEffect = {
		// 		rotate: 30,
		// 		grayscale: false,
		// 	};
		// }

		return settings;
	}

	#getDataAttributes(element) {
		const data = {};

		// Converteer alle data-* attributes naar camelCase object
		Array.from(element.attributes).forEach(attr => {
			if (attr.name.startsWith('data-')) {
				const key = attr.name
					.substring(5)
					.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());

				// Probeer JSON te parsen, anders als string
				try {
					data[key] = JSON.parse(attr.value);
				} catch {
					data[key] = attr.value;
				}
			}
		});

		return data;
	}

	#createSwiper(element, container, settings) {
		if (!container?.id) {
			const {navigation, ...settingsWithoutNav} = settings;
			return new Swiper(element, settingsWithoutNav);
		}
		this.#updateNavigationSelectors(container, settings);
		return new Swiper(element, settings);
	}

	#updateNavigationSelectors(container, settings) {
		const containerId = container.id;
		const selectors = {
			next: `swiper-button-next-${containerId}`,
			prev: `swiper-button-prev-${containerId}`,
			scrollbar: `swiper-scrollbar-${containerId}`,
			pagination: `swiper-pagination-${containerId}`
		};

		// Update navigation
		if (settings.navigation) {
			const nextEls = container.querySelectorAll(settings.navigation.nextEl);
			const prevEls = container.querySelectorAll(settings.navigation.prevEl);

			nextEls.forEach(el => el.classList.add(selectors.next));
			prevEls.forEach(el => el.classList.add(selectors.prev));

			settings.navigation.nextEl = `.${selectors.next}`;
			settings.navigation.prevEl = `.${selectors.prev}`;
		}

		// Update scrollbar
		if (settings.scrollbar) {
			const scrollbarEl = container.querySelector(settings.scrollbar.el);
			scrollbarEl?.classList.add(selectors.scrollbar);
			settings.scrollbar.el = `.${selectors.scrollbar}`;
		}

		// Update pagination
		if (settings.pagination) {
			const paginationEl = container.querySelector(settings.pagination.el);
			paginationEl?.classList.add(selectors.pagination);
			settings.pagination.el = `.${selectors.pagination}`;
		}
	}

	#hideNavigationIfNeeded(element, swiper, settings) {
		if (swiper.snapGrid.length <= 1) {
			const container = element.closest(settings.wrapperEl);
			container?.classList.add('swiper-hide-navigation');
		}
	}

	#updateSnapOffset() {
		if (!this.el.hasAttribute('data-snap-start')) return;
		const lastSlide = this.slides[this.slides.length - 1];
		if (!lastSlide) return;

		this.params.slidesOffsetAfter = this.isHorizontal()
			? this.el.offsetWidth - lastSlide.offsetWidth
			: this.el.offsetHeight - lastSlide.offsetHeight;
		this.update();
	}

	#defaultSettings() {
		return {
			wrapperEl: '.swiper-container',
			modules: [Navigation, Thumbs, Pagination, Scrollbar, Autoplay, FreeMode],
			slidesPerView: 'auto',
			grabCursor: true,
			watchSlidesProgress: true,
			scrollbar: {
				el: '.swiper-scrollbar',
				hide: false,
			},
			pagination: {
				el: '.swiper-pagination',
				clickable: true,
				renderBullet: (index, className) => `<span class="${className}"></span>`,
			},
			navigation: {
				nextEl: '.swiper-button-next',
				prevEl: '.swiper-button-prev',
				addIcons: false,
			},
			thumbs: {
				autoScrollOffset: 1,
				multipleActiveThumbs: false,
			},
			on: {
				init: this.#updateSnapOffset,
				resize: this.#updateSnapOffset,
			}
		};
	}

	async #cloneSlides(element, minSlides, swiper) {
		const wrapper = element.querySelector('.swiper-wrapper');

		if (!wrapper) {
			return;
		}

		const slides = wrapper.querySelectorAll('.swiper-slide');
		const slideCount = slides.length;
		const cloneCount = Math.ceil(minSlides / slideCount);
		const originalHtml = wrapper.innerHTML;

		await new Promise((resolve, reject) => {
			for (let i = 1; i < cloneCount; i++) {
				wrapper.insertAdjacentHTML('beforeend', originalHtml);
			}
			let requiredAmount = cloneCount * slideCount;

			let checkSlides = () => wrapper.querySelectorAll('.swiper-slide').length == requiredAmount;

			let valid = false;
			let count = 0;

			while (!valid) {
				if (count > 100) {
					console.error('Failed to clone slides in time');
					reject(false);
					return;
				}
				valid = checkSlides();
				count++;
			}

			resolve(true);
		});
	}
}

export default SwiperCarousel;
